import { StrictMode, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RedirectIfSignedIn, RequireAuth, Shell } from "./components/layout";
import { Toaster } from "./components/ui";
import { ForgotPassword, Login, ResetPassword, Signup } from "./pages/auth";
import { Onboarding, OnboardingApp, OnboardingInstall } from "./pages/onboarding";
import { ConnectAuthorize } from "./pages/connect-authorize";
import { Portfolio } from "./pages/portfolio";
import { NewApp } from "./pages/app-new";
import { Overview } from "./pages/overview";
import { Friction } from "./pages/friction";
import { Converters } from "./pages/converters";
import { Speed } from "./pages/speed";

// Heavy pages load on demand (Shell wraps its <Outlet> in <Suspense>). Tasks is lazy too because
// it imports from the funnels module, which would otherwise pull funnels back into the main chunk.
const Funnels = lazy(() => import("./pages/funnels").then((m) => ({ default: m.Funnels })));
const Tasks = lazy(() => import("./pages/tasks").then((m) => ({ default: m.Tasks })));
const Cases = lazy(() => import("./pages/cases").then((m) => ({ default: m.Cases })));
const Stories = lazy(() => import("./pages/stories").then((m) => ({ default: m.Stories })));
const SharedPage = lazy(() => import("./pages/shared").then((m) => ({ default: m.SharedPage })));
const Outcomes = lazy(() => import("./pages/outcomes").then((m) => ({ default: m.Outcomes })));
const Journeys = lazy(() => import("./pages/journeys").then((m) => ({ default: m.Journeys })));
const Heatmaps = lazy(() => import("./pages/heatmaps").then((m) => ({ default: m.Heatmaps })));
const Replays = lazy(() => import("./pages/replays").then((m) => ({ default: m.Replays })));
const ConnectAi = lazy(() => import("./pages/connect-ai").then((m) => ({ default: m.ConnectAi })));
const Surveys = lazy(() => import("./pages/surveys").then((m) => ({ default: m.Surveys })));
const Studies = lazy(() => import("./pages/studies").then((m) => ({ default: m.Studies })));
const Security = lazy(() => import("./pages/security").then((m) => ({ default: m.Security })));
const AuditLog = lazy(() => import("./pages/audit").then((m) => ({ default: m.AuditLog })));
const Releases = lazy(() => import("./pages/releases").then((m) => ({ default: m.Releases })));
const ReleaseDetail = lazy(() => import("./pages/releases").then((m) => ({ default: m.ReleaseDetail })));
const Designs = lazy(() => import("./pages/designs").then((m) => ({ default: m.Designs })));
const DesignEvidencePage = lazy(() => import("./pages/designs").then((m) => ({ default: m.DesignEvidencePage })));
const DataExport = lazy(() => import("./pages/data-export").then((m) => ({ default: m.DataExport })));
import { Feedback } from "./pages/feedback";
import { Retention } from "./pages/retention";
import { Events } from "./pages/events";
import { Goals } from "./pages/goals";
import { Integrations } from "./pages/integrations";
import { Fixes } from "./pages/fixes";
import { AppSettings } from "./pages/app-settings";
import { AcceptInvite, Workspace } from "./pages/workspace";
import { Settings } from "./pages/settings";
import { Notifications } from "./pages/notifications";
import { Today } from "./pages/today";
import { registerServiceWorker } from "./lib/push";
import { RequireAdmin } from "./pages/admin/components";
import { AdminOverview } from "./pages/admin/overview";
import { AdminAccount, AdminAccounts } from "./pages/admin/accounts";
import { AdminPeople, AdminPerson } from "./pages/admin/people";
import { AdminBooking, AdminBookings } from "./pages/admin/bookings";
import { AdminScheduling } from "./pages/admin/scheduling";
import { AdminPlays } from "./pages/admin/plays";
import { AdminOutreach } from "./pages/admin/outreach";
import { BillingPage } from "./pages/billing";
import { AdminAi } from "./pages/admin/ai";
import { AdminAiCredits } from "./pages/admin/ai-credits";
import { AdminAiModels } from "./pages/admin/ai-models";
import { AdminAccess } from "./pages/admin/access";
import { AdminSocial } from "./pages/admin/social";
import { AdminEmails } from "./pages/admin/emails";
import { isCapture } from "./lib/capture";

// Apply the saved theme before first paint (the Release posts composer sets it when photographing).
try {
  const t = isCapture ? null : localStorage.getItem("mk-theme");
  if (t) document.documentElement.dataset.theme = t;
} catch {
  /* storage blocked */
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

// The Release posts composer waits for this to reach 0 before photographing the screen.
if (isCapture) (window as Window & { metrickleBusy?: () => number }).metrickleBusy = () => queryClient.isFetching() + queryClient.isMutating();

// Ark's dialogs, popovers and menus close on a press or focus outside them, and inside a frame they
// count the parent page too: pressing "Take shot" closed the modal being photographed. With no
// frameElement they only listen inside the frame.
if (isCapture) Object.defineProperty(window, "frameElement", { configurable: true, get: () => null });

const router = createBrowserRouter([
  // A read-only link from someone's workspace: no sign-in, no shell.
  {
    path: "/share/:token",
    element: (
      <Suspense fallback={null}>
        <SharedPage />
      </Suspense>
    ),
  },
  // Signed in or not: the emailed reset link has to work in a browser that's still signed in.
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/reset-password", element: <ResetPassword /> },
  {
    element: <RedirectIfSignedIn />,
    children: [
      { path: "/login", element: <Login /> },
      { path: "/signup", element: <Signup /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      { path: "/onboarding", element: <Onboarding /> },
      { path: "/onboarding/app", element: <OnboardingApp /> },
      { path: "/onboarding/install/:appId", element: <OnboardingInstall /> },
      { path: "/invite/:invitationId", element: <AcceptInvite /> },
      // OAuth consent for assistants connecting to the MCP server (apps/api/src/oauth).
      { path: "/connect/authorize", element: <ConnectAuthorize /> },
      {
        element: <Shell />,
        children: [
          { path: "/", element: <Portfolio /> },
          { path: "/workspace", element: <Workspace /> },
          { path: "/workspace/billing", element: <BillingPage /> },
          { path: "/workspace/security", element: <Security /> },
          { path: "/workspace/audit", element: <AuditLog /> },
          { path: "/workspace/data-export", element: <DataExport /> },
          { path: "/connect-ai", element: <ConnectAi /> },
          { path: "/settings", element: <Settings /> },
          { path: "/notifications", element: <Notifications /> },
          { path: "/today", element: <Today /> },
          { path: "/outcomes", element: <Outcomes /> },
          { path: "/apps/new", element: <NewApp /> },
          { path: "/apps/:appId", element: <Overview /> },
          { path: "/apps/:appId/releases", element: <Releases /> },
          { path: "/apps/:appId/releases/:releaseId", element: <ReleaseDetail /> },
          { path: "/apps/:appId/designs", element: <Designs /> },
          { path: "/apps/:appId/designs/:linkId", element: <DesignEvidencePage /> },
          { path: "/apps/:appId/funnels", element: <Funnels /> },
          { path: "/apps/:appId/tasks", element: <Tasks /> },
          { path: "/apps/:appId/cases", element: <Cases /> },
          { path: "/apps/:appId/stories", element: <Stories /> },
          { path: "/apps/:appId/journeys", element: <Journeys /> },
          { path: "/apps/:appId/friction", element: <Friction /> },
          { path: "/apps/:appId/speed", element: <Speed /> },
          { path: "/apps/:appId/converters", element: <Converters /> },
          { path: "/apps/:appId/heatmaps", element: <Heatmaps /> },
          { path: "/apps/:appId/replays", element: <Replays /> },
          { path: "/apps/:appId/surveys", element: <Surveys /> },
          { path: "/apps/:appId/studies", element: <Studies /> },
          { path: "/apps/:appId/feedback", element: <Feedback /> },
          { path: "/apps/:appId/retention", element: <Retention /> },
          { path: "/apps/:appId/events", element: <Events /> },
          { path: "/apps/:appId/goals", element: <Goals /> },
          { path: "/apps/:appId/integrations", element: <Integrations /> },
          { path: "/apps/:appId/fixes", element: <Fixes /> },
          { path: "/apps/:appId/settings", element: <AppSettings /> },
          // Platform-owner growth console (PLATFORM_ADMIN_EMAILS only).
          { path: "/admin", element: <RequireAdmin><AdminOverview /></RequireAdmin> },
          { path: "/admin/accounts", element: <RequireAdmin><AdminAccounts /></RequireAdmin> },
          { path: "/admin/accounts/:accountId", element: <RequireAdmin><AdminAccount /></RequireAdmin> },
          { path: "/admin/people", element: <RequireAdmin><AdminPeople /></RequireAdmin> },
          { path: "/admin/people/:personId", element: <RequireAdmin><AdminPerson /></RequireAdmin> },
          { path: "/admin/bookings", element: <RequireAdmin><AdminBookings /></RequireAdmin> },
          { path: "/admin/bookings/:bookingId", element: <RequireAdmin><AdminBooking /></RequireAdmin> },
          { path: "/admin/scheduling", element: <RequireAdmin><AdminScheduling /></RequireAdmin> },
          { path: "/admin/plays", element: <RequireAdmin><AdminPlays /></RequireAdmin> },
          { path: "/admin/outreach", element: <RequireAdmin><AdminOutreach /></RequireAdmin> },
          { path: "/admin/ai", element: <RequireAdmin><AdminAi /></RequireAdmin> },
          { path: "/admin/ai-credits", element: <RequireAdmin><AdminAiCredits /></RequireAdmin> },
          { path: "/admin/ai-models", element: <RequireAdmin><AdminAiModels /></RequireAdmin> },
          { path: "/admin/access", element: <RequireAdmin><AdminAccess /></RequireAdmin> },
          { path: "/admin/social", element: <RequireAdmin><AdminSocial /></RequireAdmin> },
          { path: "/admin/emails", element: <RequireAdmin><AdminEmails /></RequireAdmin> },
        ],
      },
    ],
  },
]);

// Push notifications and installing the app (static/sw.js).
registerServiceWorker();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {!isCapture && <Toaster />}
    </QueryClientProvider>
  </StrictMode>,
);
